import { Config } from '../video-data/config';
import type { SteppedCurveDescription } from './SteppedCurve';
import { SteppedCurve } from './SteppedCurve';
import type { LinearCurveDescription } from './LinearCurve';
import { LinearCurve } from './LinearCurve';
import type { BezierCurveDescription } from './BezierCurve';
import { BezierCurve } from './BezierCurve';

import { getProperty, propertyExists, setProperty } from './properties';
import type { Anchor, BaseCurveDescription, Curve } from './Curve';
import type { AnimationCurveGroup } from './AnimationCurveGroup';

type Bounds = {
    smallestX: number;
    largestX: number;
};

/**
 * Object that can be driven by an animation curve.
 * Properties are dynamically accessed via property paths.
 * The index signature is needed to accept both plain objects (in tests)
 * and class instances (in production code).
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export interface DrivenObject extends Record<string, any> {
    destroyed?: boolean;
    name?: string;
}

export type CurveDescription =
    | LinearCurveDescription
    | BezierCurveDescription
    | SteppedCurveDescription;

export type AnimationCurveDescription = {
    duration: number;
    curve: CurveDescription;
    drivenRange: [number, number][] | [number, number];
    drivenProperty: string[];
    shouldOffsetPropertyValue: boolean | boolean[];
};

/**
 * This is a flat structure with curve properties spread at the top level,
 * plus driven property information. Different from AnimationCurveDescription
 * which has a nested `curve` property (used by setCurveDescription).
 */
export type AnimationCurveGetDescription = BaseCurveDescription & {
    drivenProperty: string[][];
    drivenRange: [number, number][];
    shouldOffsetPropertyValue: boolean[];
};

export class AnimationCurve {
    classType = 'AnimationCurve';
    group: AnimationCurveGroup = null;
    name = 'curve_1';
    index = 0;
    curve: Curve = null;
    _percentage = 0;
    percentageOffset = 0;

    _drivenObject: DrivenObject[] = [];
    _drivenProperty: string[][] = [];
    _drivenRange: [number, number][] = [];
    _drivenValue: number[] = [];
    _shouldOffsetPropertyValue: boolean[] = [];

    fullName(): string {
        let name = this.name;
        name += '_' + this.index;
        this._drivenProperty.forEach((property) => {
            name += '_' + property.join('_');
        });
        return name;
    }

    set percentage(value: number) {
        if (this.curve === null) {
            throw Error('No curve has been specified yet. Unable to evaluate it.');
        }
        if (isNaN(value)) {
            throw Error(`${value} is not a valid value. Expected a number between 0 and 1.`);
        }
        this.curve.percentage = value;
        this._updateDriven();
    }

    get percentage(): number {
        return this._percentage;
    }

    get value(): number {
        if (this.curve === null) {
            throw Error('No curve has been specified yet. Unable to evaluate it.');
        }
        return this.curve.value;
    }

    _updateDriven() {
        this._drivenObject.forEach((d, i) => {
            // verify the object's property
            if (d.destroyed) {
                return;
            }

            // get new value
            let propertyValue =
                this._drivenRange[i][0] +
                (this._drivenRange[i][1] - this._drivenRange[i][0]) * this.curve.value;
            if (this._shouldOffsetPropertyValue[i]) {
                propertyValue += this._drivenValue[i];
            }

            // set new value
            try {
                setProperty(this._drivenObject[i], this._drivenProperty[i], propertyValue);
            } catch (err) {
                // In this case we're unable to the set the property. This can happen when the object is destroyed
                console.warn(
                    `Unable to set property ${this._drivenProperty[i]} on object ${this._drivenObject[i]?.name}`,
                );
            }
        });
    }

    addDriven(
        object: DrivenObject,
        properties: string | string[],
        range: [number, number],
        shouldOffsetValue: boolean,
    ) {
        if (object === undefined || object === null) {
            throw Error('Unable to use empty Object');
        }
        if (properties === undefined) {
            throw Error('Properties needs to be defined');
        }
        if (typeof properties === 'string') {
            properties = [properties];
        }
        if (!propertyExists(object, properties)) {
            throw Error(`${properties.join('.')} is not a property of ${object.name}`);
        }
        if (range === undefined) {
            range = [0, 1];
        }

        const initValue = getProperty(object, properties) as number;
        this._drivenObject.push(object);
        this._drivenProperty.push(properties);
        this._drivenRange.push(range);
        this._drivenValue.push(initValue);
        this._shouldOffsetPropertyValue.push(shouldOffsetValue);
    }

    info() {
        const info = this._drivenObject.map((drivenObject, index) => {
            return {
                index,
                property: this._drivenProperty[index],
                range: this._drivenRange[index],
                default: this._drivenValue[index],
                current: getProperty(drivenObject, this._drivenProperty[index]),
            };
        });
        return info;
    }

    infoAsMessage(): string {
        let message = `(`;
        if (this.group) {
            message += `group: ${this.group.name}, `;
        }
        message += `name: ${this.name}, `;
        message += 'object: [';
        this._drivenObject.forEach((o) => {
            if (o && o.hasOwnProperty('name')) {
                message += o.name;
            } else {
                message += 'null';
            }
        });
        message += '], ';
        message += `property:[`;
        this._drivenProperty.forEach((p) => {
            message += `${p}`;
        });
        message += '])';
        return message;
    }

    getCurveDescription(): AnimationCurveGetDescription {
        return {
            ...this.curve.getCurveDescription(),
            drivenProperty: this._drivenProperty,
            drivenRange: this._drivenRange,
            shouldOffsetPropertyValue: this._shouldOffsetPropertyValue,
        };
    }

    setCurveDescription(description: AnimationCurveDescription, drivenObjects: DrivenObject[]) {
        // NOTE: if we want to make our drivenObjects part of our description
        // we'll have to start assigning 'unique' names to our class instances
        if (!description.hasOwnProperty('curve')) {
            throw Error('Description invalid,  expected it to contain curve object.');
        }

        // create curve
        switch (description.curve.classType) {
            case 'BezierCurve':
                this.curve = BezierCurve.fromDescription(description.curve);
                break;
            case 'LinearCurve':
                this.curve = LinearCurve.fromDescription(description.curve);
                break;
            case 'SteppedCurve':
                this.curve = SteppedCurve.fromDescription(description.curve);
                break;
            default:
                throw Error(
                    `Unknown curve type. Currently we only support BezierCurve, LinearCurve and SteppedCurve.`,
                );
        }

        // assign to driven
        drivenObjects.forEach((drivenObject) => {
            if (!drivenObject) {
                return;
            }
            description.drivenProperty.forEach((drivenProperty, i) => {
                if (!drivenObject) {
                    return;
                }
                if (!drivenProperty) {
                    return;
                }
                let drivenRange: [number, number];

                if (Array.isArray(description.drivenRange[i])) {
                    drivenRange = description.drivenRange[i] as [number, number];
                } else {
                    drivenRange = description.drivenRange as [number, number];
                }
                let shouldOffsetPropertyValue: boolean;

                if (Array.isArray(description.shouldOffsetPropertyValue)) {
                    shouldOffsetPropertyValue = description.shouldOffsetPropertyValue[i];
                } else {
                    shouldOffsetPropertyValue = description.shouldOffsetPropertyValue;
                }

                const drivenPropertyTokens = drivenProperty.split('.');
                if (!propertyExists(drivenObject, drivenPropertyTokens)) {
                    return;
                }
                this.addDriven(
                    drivenObject,
                    drivenPropertyTokens,
                    drivenRange,
                    shouldOffsetPropertyValue,
                );
            });
        });

        this.duration = description.duration;
    }

    reset() {
        // reset all driven properties to their default values
        this._drivenObject.forEach((drivenObject, index) => {
            // make sure the object and its property hierarchy still exist when clearing the animation
            // NOTE: the _destroyed property is set to true when a LUMINARY/PIXI object is deleted.
            if (!drivenObject || drivenObject.destroyed === true) {
                // TODO: if it no longer exists, ensure we delete the entry
                return;
            }
            try {
                setProperty(drivenObject, this._drivenProperty[index], this._drivenValue[index]);
            } catch (err) {
                // this happens when we destroy Luminary for example. As all elements get deleted it can happen that the
                // driven objects get deleted before the animation curves which will cause this error. This is harmless.
                console.warn(
                    `Unable to reset property ${this._drivenProperty[index]} on object ${drivenObject?.name}`,
                );
            }
        });
    }

    /**
     * Returns the driven ranges. These are the start and end values for each driven property. We interpolate between
     * them as we move along the curve using percentage.
     */
    get drivenRange(): [number, number][] {
        return this._drivenRange;
    }

    clear() {
        this.reset();

        this._drivenObject = [];
        this._drivenProperty = [];
        this._drivenRange = [];
        this._drivenValue = [];
        this._shouldOffsetPropertyValue = [];
        this.group = null;
    }

    destroy() {
        if (this.curve) {
            this.curve.destroy();
            this.curve = null;
        }
        this.clear();
    }

    set duration(value: number) {
        this.curve.duration = value;
    }

    get duration(): number {
        return this.curve.duration;
    }

    time2Percentage(time: number) {
        return this.curve.time2Percentage(time);
    }

    getPoints(anchor: Anchor) {
        return this.curve.getPoints(anchor);
    }

    getEndPoints(anchor = Config.ALL_ANIMATION_CURVE_ANCHOR) {
        return this.curve.getEndPoints(anchor);
    }

    getEndPointPosition(index: number) {
        return this.curve.getEndPointPosition(index);
    }

    smallestX(anchors = Config.ALL_ANIMATION_CURVE_ANCHOR) {
        return this.curve.smallestX(anchors);
    }

    largestX(anchors = Config.ALL_ANIMATION_CURVE_ANCHOR) {
        return this.curve.largestX(anchors);
    }

    hasAnchors(anchor = Config.START_ANIMATION_CURVE_ANCHOR) {
        return this.curve.getPoints(anchor).length !== 0;
    }

    scaleUsingBounds(
        scaleFactor: number,
        bounds: Bounds,
        tangents = true,
        anchors = Config.ALL_ANIMATION_CURVE_ANCHOR,
    ) {
        this.curve.scaleUsingBounds(scaleFactor, bounds, tangents, anchors);
    }

    move(x: number, y: number, anchor = Config.ALL_ANIMATION_CURVE_ANCHOR) {
        this.curve.move(x, y, anchor);
    }

    static fromDescription(description: AnimationCurveDescription, drivenObjects: DrivenObject[]) {
        const instance = new AnimationCurve();
        instance.setCurveDescription(description, drivenObjects);
        return instance;
    }
}
